import {useEffect,useRef,useState} from 'react';
import {useLanguage} from '@/i18n/LanguageProvider';
import {supabase} from '@/integrations/supabase/client';
import {connectedFileReadSessionId} from '@/lib/connectedFileRead';
import {WindsorReportWorkspace} from '@/components/connections/WindsorReportWorkspace';
import type {MirrorWindsorReportOrigin} from '@/lib/mirrorWindsorReportOrigin';

export function MirrorWindsorReportLauncher({origin,onCancel}: {
  origin:MirrorWindsorReportOrigin;onCancel:()=>void;
}) {
  const {t}=useLanguage(),[open,setOpen]=useState(false),[busy,setBusy]=useState(false);
  const mounted=useRef(true),ticket=useRef(0),active=useRef(origin),cancel=useRef(onCancel);active.current=origin;cancel.current=onCancel;
  const current=()=>mounted.current&&active.current===origin&&origin.isCurrent();
  useEffect(()=>{
    mounted.current=true;
    const lifecycle=ticket,mountState=mounted;
    const {data:{subscription}}=supabase.auth.onAuthStateChange((_event,session)=>{
      if(session?.user.id!==origin.ownerId||connectedFileReadSessionId(session?.access_token??'')!==origin.authSessionId) {
        ticket.current++;setOpen(false);cancel.current();
      }
    });
    return()=>{mountState.current=false;lifecycle.current++;subscription.unsubscribe();};
  },[origin]);
  const launch=async()=>{
    if(busy||open||!current())return;
    const attempt=++ticket.current;setBusy(true);
    try {
      const result=await supabase.auth.getSession();
      if(!current()||attempt!==ticket.current)return;
      const session=result.data.session;
      if(result.error||session?.user.id!==origin.ownerId
        ||connectedFileReadSessionId(session?.access_token??'')!==origin.authSessionId){onCancel();return;}
      setOpen(true);
    } catch {
      if(current())onCancel();
    } finally {
      if(current()&&attempt===ticket.current)setBusy(false);
    }
  };
  if(!current())return null;
  if(open)return <WindsorReportWorkspace ownerId={origin.ownerId} expectedAuthSessionId={origin.authSessionId}
    isCurrent={current} onCancel={onCancel}/>;
  return <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
    <button type="button" disabled={busy} onClick={()=>void launch()}
      className="min-h-11 w-full rounded-lg border border-primary/30 px-3 py-2 text-sm text-primary disabled:opacity-50">
      {t('windsorReport.open')}
    </button>
    <button type="button" disabled={busy} onClick={onCancel}
      className="min-h-11 w-full px-3 py-2 text-sm text-muted-foreground">{t('common.cancel')}</button>
  </div>;
}
